<script setup lang="ts">
import { ref, computed, onMounted } from "vue"
import FullCalendar from "@fullcalendar/vue3"
import dayGridPlugin from "@fullcalendar/daygrid"
import timeGridPlugin from "@fullcalendar/timegrid"
import listPlugin from "@fullcalendar/list"
import type { CalendarOptions, EventInput, EventHoveringArg } from "@fullcalendar/core"
import { CalendarAPI } from "@/js/api/calendar"
import type { EventDTO } from "@/js/types/generated"
import EventFeed from "@/js/components/EventFeed.vue"
import EventTooltip from "@/js/components/EventTooltip.vue"
import { getVisibleDateRange } from "@/js/utils/getVisibleDateRange.ts"
import { getViewTitle } from "@/js/utils/getViewTitle"
import { getSlotTimeRange } from "@/js/utils/getSlotTimeRange"
import { addDays, addMonths, addWeeks, format, parseISO } from "date-fns"
import type { CalendarDisplay, CalendarView } from "@/js/types/calendar"

const props = withDefaults(
	defineProps<{
		display?: CalendarDisplay
		view?: CalendarView
		compact?: boolean
		perPage?: number
		categories?: string[]
		initialEvents?: EventDTO[]
	}>(),
	{
		display: "calendar",
		view: "month",
		compact: false,
		perPage: 10,
		categories: () => [],
		initialEvents: () => [],
	},
)

const showCalendar = computed(() => props.display !== "list")
const showList = computed(() => props.display !== "calendar")

const api = new CalendarAPI()
const calendarEvents = ref<EventInput[]>([])
const feedEvents = ref<EventDTO[]>([])
const isLoading = ref(false)

// Tooltip state
const tooltipReference = ref<HTMLElement | null>(null)
const hoveredEvent = ref<EventDTO | null>(null)
const currentDate = ref(new Date())

const fullCalendarViews = {
	month: "dayGridMonth",
	week: "timeGridWeek",
	day: "timeGridDay",
} as const satisfies Record<CalendarView, string>

// Seven time-grid columns are unreadable when narrow; the other views cope with smaller cells.
const compactFullCalendarViews = {
	...fullCalendarViews,
	week: "listWeek",
} as const satisfies Record<CalendarView, string>

const fullCalendarView = computed(() => (props.compact ? compactFullCalendarViews : fullCalendarViews)[props.view])
// Compact month cells are too narrow for times, and list views have their own time column.
const showTimePrefix = computed(() => !props.compact || props.view === "day")

const stepDate = {
	month: addMonths,
	week: addWeeks,
	day: addDays,
} as const satisfies Record<CalendarView, (date: Date, amount: number) => Date>

const title = computed(() => getViewTitle(props.view, currentDate.value))
const calendarKey = computed(() => format(currentDate.value, "yyyy-MM-dd"))

function formatEventTime(dateString: string): string {
	const date = new Date(dateString)
	return date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
}

const handleResponse = (events: EventDTO[]) => {
	feedEvents.value = events
	calendarEvents.value = events.map((event) => ({
		id: `${event.id}-${event.start}`,
		title: event.title,
		start: event.start,
		// FullCalendar treats all-day ends as exclusive; ours are inclusive.
		end: event.end && event.allDay ? addDays(parseISO(event.end), 1) : (event.end ?? undefined),
		allDay: event.allDay,
		url: event.url,
		backgroundColor: event.color ?? undefined,
		borderColor: event.color ?? undefined,
		extendedProps: {
			formattedTime: event.allDay ? "" : formatEventTime(event.start),
		},
	}))
}

async function fetchEvents(date: Date) {
	isLoading.value = true
	try {
		const { startDate, endDate } = getVisibleDateRange(props.view, date)
		const response = await api.index({ startDate, endDate, categories: props.categories })
		handleResponse(response.events)
	} finally {
		isLoading.value = false
	}
}

async function navigateTo(date: Date) {
	if (isLoading.value) return

	await fetchEvents(date)
	currentDate.value = date
}

async function handlePrev() {
	await navigateTo(stepDate[props.view](currentDate.value, -1))
}

async function handleNext() {
	await navigateTo(stepDate[props.view](currentDate.value, 1))
}

async function handleToday() {
	await navigateTo(new Date())
}

function handleEventMouseEnter(info: EventHoveringArg) {
	const compositeId = info.event.id
	const separatorIndex = compositeId.indexOf("-")
	const eventId = Number(compositeId.slice(0, separatorIndex))
	const eventStart = compositeId.slice(separatorIndex + 1)
	hoveredEvent.value = feedEvents.value.find((e) => e.id === eventId && e.start === eventStart) ?? null
	tooltipReference.value = info.el
}

function handleEventMouseLeave() {
	hoveredEvent.value = null
	tooltipReference.value = null
}

const calendarOptions = computed<CalendarOptions>(() => ({
	plugins: [dayGridPlugin, timeGridPlugin, listPlugin],
	initialView: fullCalendarView.value,
	initialDate: currentDate.value,
	events: calendarEvents.value,
	eventDisplay: "block",
	headerToolbar: false,
	height: "auto",
	nowIndicator: true,
	...getSlotTimeRange(feedEvents.value),
	dayMaxEvents: props.compact ? 2 : false,
	eventContent: (arg) => {
		const time = showTimePrefix.value ? (arg.event.extendedProps.formattedTime as string) : ""
		const title = arg.event.title
		return {
			html: time ? `<span class="sno-cal-event-time">${time}</span> ${title}` : title,
		}
	},
	eventClick: (info) => {
		if (info.event.url) {
			info.jsEvent.preventDefault()
			window.open(info.event.url, "_blank")
		}
	},
	eventMouseEnter: handleEventMouseEnter,
	eventMouseLeave: handleEventMouseLeave,
}))

onMounted(() => {
	handleResponse(props.initialEvents)
})
</script>

<template>
	<div class="sno-calendar" :class="{ 'sno-cal-compact': compact }">
		<div class="flex flex-wrap-reverse items-center gap-x-4 gap-y-3 mb-5">
			<div class="flex items-center gap-2">
				<button
					type="button"
					class="rounded-md border border-[color:var(--sno-cal-button-border,#d1d5db)] bg-[color:var(--sno-cal-button-bg,#f9fafb)] px-3 py-2 text-sm font-medium text-[color:var(--sno-cal-button-text,#374151)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="isLoading"
					@click="handlePrev"
				>
					←
				</button>
				<button
					type="button"
					class="rounded-md border border-[color:var(--sno-cal-button-border,#d1d5db)] bg-[color:var(--sno-cal-button-bg,#f9fafb)] px-3 py-2 text-sm font-medium text-[color:var(--sno-cal-button-text,#374151)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="isLoading"
					@click="handleNext"
				>
					→
				</button>
				<button
					type="button"
					class="rounded-md border border-[color:var(--sno-cal-button-border,#d1d5db)] bg-[color:var(--sno-cal-button-bg,#f9fafb)] px-3 py-2 text-sm font-medium text-[color:var(--sno-cal-button-text,#374151)] hover:brightness-95 disabled:opacity-50 disabled:cursor-not-allowed"
					:disabled="isLoading"
					@click="handleToday"
				>
					Today
				</button>
				<div class="text-sm text-gray-500" :class="{ invisible: !isLoading }" :aria-hidden="!isLoading">
					Loading...
				</div>
			</div>
			<div role="heading" aria-level="2" class="sno-cal-title grow basis-72 text-center">{{ title }}</div>
		</div>
		<FullCalendar v-if="showCalendar" :key="calendarKey" :options="calendarOptions" />
		<EventFeed v-if="showList" :events="feedEvents" :per-page="perPage" :view="view" :title="title" />

		<EventTooltip v-if="hoveredEvent && tooltipReference" :event="hoveredEvent" :reference-el="tooltipReference" />
	</div>
</template>
